/**
 * Shapes of the Terify admin endpoints (apps/backend/src/api/admin/terify).
 * Standard Medusa resources use @medusajs/types (HttpTypes) instead.
 */

export const AREAS = ["orders", "draft_orders", "refunds", "products", "inventory", "gift_cards", "customers", "customer_privacy", "subscriptions", "recovery_settings", "reviews", "upsells", "discounts", "content", "analytics", "staff", "settings", "system_status"] as const
export type Area = (typeof AREAS)[number]
/** "logs:read" is not a role permission: the backend grants it to the owner and TERIFY_LOG_VIEWERS only. */
export type Permission = `${Area}:${"read" | "write"}` | "logs:read"
export type Role = "owner" | "admin" | "staff"

export const AREA_LABELS: Record<Area, string> = {
  orders: "Orders",
  draft_orders: "Draft orders",
  refunds: "Refunds",
  products: "Products",
  inventory: "Inventory",
  gift_cards: "Gift cards",
  customers: "Customers",
  customer_privacy: "Customer data requests",
  subscriptions: "Subscriptions",
  recovery_settings: "Recovery settings",
  reviews: "Reviews",
  upsells: "Cart & Checkout",
  discounts: "Discounts",
  content: "Content",
  analytics: "Analytics",
  staff: "Staff and permissions",
  settings: "Store settings",
  system_status: "System status",
}

/** Areas with nothing to edit: the permission picker shows View only. */
export const VIEW_ONLY_AREAS: readonly Area[] = ["system_status"]

/** One line under each area in the permission picker, as on Shopify's staff page. View / Edit spelled out where they differ. */
export const AREA_HELP: Record<Area, string> = {
  orders: "Orders, abandoned checkouts and saved order views. Edit: fulfil, edit items, returns, mark as paid, archive, cancel",
  draft_orders: "Draft orders. Edit: create drafts, create orders for customers, send invoices, mark as paid",
  refunds: "Issue refunds on orders (needs Orders too)",
  products: "Products, variants and collections. Edit: bulk editor, import, duplicate, collection order",
  inventory: "Stock levels and adjustment history. Edit: change stock",
  gift_cards: "Gift cards and balances. Edit: issue cards and adjust balances",
  customers: "Customer profiles, addresses, segments and export. Edit: add, import, tag and delete customers",
  customer_privacy: "View: download all data stored about a customer. Edit: erase a customer's personal data (can't be undone)",
  subscriptions: "Subscriptions, skips and failed charges, and the Recovery app's customers, messages and sends. Edit: cancel, pause or apply the win-back offer for a customer (refunds need Refunds)",
  recovery_settings: "Recovery app settings: sending limits, dates and offer, tiers, the kill switch, Klaviyo card emails and the Recharge connection. A separate Klaviyo account needs full permissions",
  reviews: "Product and store reviews, moderation, import and review requests",
  upsells: "Cart and checkout offers, Shipping Protection, the reservation timer and product blocks. Edit: create offers, set offer and protection prices",
  discounts: "Discount codes and automatic discounts. Edit: create, duplicate, activate and share links",
  content: "Pages, blog posts, menus, redirects, files and custom data",
  analytics: "Home dashboard, live visitors, Live View and reports",
  staff: "Staff list, activity log. Edit: invite, change permissions, sign out, reset two-step verification",
  settings: "Store settings, markets, shipping, taxes, payments, checkout, notifications and apps, and Subscriptions → Settings (offers and tiers, retries, portal, billing descriptor)",
  system_status: "Servers, database, AWS alarms, websites, background jobs and store health (orders, issues, emails, ShipStation, renewals). For the IT team; no customer details",
}

export type Me = {
  user: { id: string; email: string; first_name: string | null; last_name: string | null; avatar_url: string | null }
  staff: { id: string; role: Role; permissions: Permission[] } | null
  store: { name: string; platform: string; order_prefix?: string; order_suffix?: string; timezone?: string; weight_unit?: string }
  areas: Area[]
  /** Terify apps switched on in Settings → Apps (drives the sidebar's Apps section). */
  apps?: { id: string; name: string; enabled: boolean }[]
  /** Two-step sign-in: verified = this session entered its code (or it's off). */
  two_factor?: { enabled: boolean; verified: boolean; recovery_left: number; required?: boolean; method?: "app" | "sms" | null; phone_hint?: string | null; sms_available?: boolean }
}

export type Kpi = { value: number; change_pct: number | null }
export type Dashboard = {
  range: "today" | "7d" | "30d" | "90d"
  period: { start: string; end: string }
  currency_code: string
  // total_sales, orders and aov are the Reports numbers (sales ledger) for sales_range.
  sales_range?: { since: string; until: string }
  kpis: { total_sales: Kpi; orders: Kpi; aov: Kpi; new_customers: Kpi }
  recent_orders: {
    id: string
    display_id: number
    email: string | null
    customer_name: string | null
    total: number
    currency_code: string
    status: string
    created_at: string
  }[]
  low_stock: {
    threshold: number
    items: { inventory_item_id: string; sku: string | null; title: string | null; location_id: string; available: number }[]
  }
}

export type StaffStatus = "active" | "deactivated"

export type StaffMember = {
  id: string
  user_id: string
  email: string
  first_name: string | null
  last_name: string | null
  avatar_url: string | null
  role: Role
  granted_permissions: Permission[]
  permissions: Permission[]
  status: StaffStatus
  last_seen_at: string | null
  created_at: string
}

export type StaffInvite = {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  role: Exclude<Role, "owner">
  granted_permissions: Permission[]
  status: "pending" | "expired"
  invited_by_email: string | null
  expires_at: string
  created_at: string
  /** Only when email isn't set up on the server yet: share this link by hand. */
  invite_url?: string
  /** Why the invitation email wasn't sent (SES refused it); share invite_url by hand. */
  email_error?: string
}

export type StaffList = { owner: StaffMember | null; staff: StaffMember[]; invites: StaffInvite[] }

export type StaffLogin = { at: string; ip: string | null; country: string | null; user_agent: string | null }
export type StaffDetail = { staff: StaffMember; logins: StaffLogin[] }

export type InviteLookup = {
  invite: { email: string; first_name: string | null; last_name: string | null; invited_by_email: string | null; expires_at: string }
  store: { name: string }
}

export type ActivityEntry = {
  id: string
  actor_id: string | null
  actor_email: string | null
  actor_type: string
  action: string
  area: Area | null
  method: string | null
  path: string | null
  resource_id: string | null
  status_code: number | null
  ip: string | null
  metadata?: Record<string, any> | null
  created_at: string
  /** Staff member's name (admin users only). */
  actor_name?: string | null
  /** The record the change touched, with its current name and admin link (null link = deleted or no page). */
  record?: { id: string; kind: string | null; label: string | null; url: string | null; deleted: boolean } | null
}

export type ActivityActor = { id: string; email: string | null; name: string | null }
